import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, LogIn, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CompleteRegistrationForm } from "@/features/auth/components/CompleteRegistrationForm";
import { authService } from "@/features/auth/services/authService";
import type { RegistrationUserInfoResponse } from "@/features/auth/types/auth.types";
import { handleApiError } from "@/lib/utils/errorHandler";

type PageStatus = "loading" | "error" | "ready" | "success";

export function CompleteRegistrationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const userId = searchParams.get("userId");

  const [status, setStatus] = useState<PageStatus>(() =>
    token && userId ? "loading" : "error"
  );
  const [error, setError] = useState<string | null>(
    token && userId
      ? null
      : "Invalid or missing registration link. Please use the link from your email."
  );
  const [userInfo, setUserInfo] = useState<RegistrationUserInfoResponse | null>(null);

  // Query params for API calls: exclude tenantId (backend get-registration-user-info only needs token, userId)
  const queryParams = Object.fromEntries(
    [...searchParams.entries()].filter(([key]) => key !== "tenantId")
  );

  useEffect(() => {
    if (!token || !userId) return;

    let cancelled = false;
    setStatus("loading");
    setError(null);

    const fetchUserInfo = async () => {
      try {
        const data = await authService.getRegistrationUserInfo(queryParams);
        if (!cancelled) {
          setUserInfo(data);
          setStatus("ready");
        }
      } catch (err) {
        if (!cancelled) {
          const apiError = handleApiError(err);
          setError(
            apiError.message ||
              "Invalid or expired link. Please contact your administrator for a new registration link."
          );
          setStatus("error");
        }
      }
    };

    fetchUserInfo();
    return () => {
      cancelled = true;
    };
  }, [token, userId, searchParams]);

  const handleRetry = () => {
    if (!token || !userId) return;
    setError(null);
    setStatus("loading");
    const fetchAgain = async () => {
      try {
        const data = await authService.getRegistrationUserInfo(queryParams);
        setUserInfo(data);
        setStatus("ready");
      } catch (err) {
        const apiError = handleApiError(err);
        setError(
          apiError.message ||
            "Invalid or expired link. Please contact your administrator for a new registration link."
        );
        setStatus("error");
      }
    };
    fetchAgain();
  };

  const handleSuccess = () => {
    setStatus("success");
    toast.success("Registration complete! Click here to login", {
      action: {
        label: "Login",
        onClick: () => window.location.assign("/login"),
      },
    });
  };

  const handleFormError = (_message: string) => {
    // Inline error is shown in the form; optionally show toast for network/validation
  };

  // Loading state
  if (status === "loading") {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-md space-y-8">
            <Card className="shadow-medium">
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-xl">Complete Registration</CardTitle>
                <CardDescription>Loading your registration details...</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground mt-4">Please wait...</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === "error") {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-md space-y-8">
            <Card className="shadow-medium">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4 rounded-full bg-destructive/10 p-4 w-fit">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                </div>
                <CardTitle className="text-xl">Registration Link Invalid</CardTitle>
                <CardDescription>
                  We couldn't load your registration. This can happen if the link has expired
                  or was already used.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error ?? "Something went wrong."}</AlertDescription>
                </Alert>
                <p className="text-sm text-muted-foreground text-center">
                  Please contact your administrator to request a new registration link.
                </p>
                <div className="flex flex-col gap-2">
                  {token && userId && (
                    <Button variant="outline" className="w-full gap-2" onClick={handleRetry}>
                      <RefreshCw className="h-4 w-4" />
                      Try Again
                    </Button>
                  )}
                  <Link to="/login">
                    <Button variant="secondary" className="w-full">
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <footer className="py-6 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} My Professional Tax. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-md space-y-8">
            <Card className="shadow-medium">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4 rounded-full bg-success/10 p-4 w-fit">
                  <CheckCircle2 className="h-8 w-8 text-success" />
                </div>
                <CardTitle className="text-xl">Registration Complete</CardTitle>
                <CardDescription>Your account is ready. You can now sign in.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert variant="success">
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertTitle>Success</AlertTitle>
                  <AlertDescription>
                    Your account has been created successfully!
                  </AlertDescription>
                </Alert>
                <Link to="/login">
                  <Button className="w-full h-11 gap-2">
                    <LogIn className="h-4 w-4" />
                    Sign in to your account
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>

        <footer className="py-6 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} My Professional Tax. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  // Ready state - show the form
  if (status === "ready" && userInfo && token) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-md space-y-8">
            <CompleteRegistrationForm
              userInfo={userInfo}
              token={token}
              queryParams={queryParams}
              onSuccess={handleSuccess}
              onError={handleFormError}
            />
          </div>
        </div>

        <footer className="py-6 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} My Professional Tax. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  return null;
}
